import { styled, type StyledDecorator } from '@glitz/react';
import * as React from 'react';
import { bareListDecorator } from 'webapp/decorators/list';
import { createFlipYDecorator, SlideHeightAnimation, useOnClickOutside } from '@avensia/excite-ui';
import { createThemeDecorator, Theme, type ThemeType } from 'webapp/theme';
import { overflowYDecorator } from 'webapp/decorators/overflow';
import { ChevronDownIcon } from '../icons';
import { scrollbarDecorator } from 'webapp/decorators/scrollbar';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import { ResetButton } from 'webapp/components/button/button';
import { createFieldSizeDecorator, FieldSize, type FieldSizeType } from 'webapp/components/field-size';
import { CloseIcon } from 'webapp/components/icons';

type PropType = {
  options: NordicNest.Api.SelectOptionApiModel[];
  value?: string;
  optionTransform?: (option: NordicNest.Api.SelectOptionApiModel) => React.ReactNode;
  selectedTransform?: (option: NordicNest.Api.SelectOptionApiModel) => React.ReactNode;
  as?: ThemeType;
  name?: string;
  onChangeOption?: (value: string) => void;
  size?: FieldSizeType;
  placeholder?: string;
  placeholderTransform?: () => React.ReactNode;
  selectedValueDecorator?: StyledDecorator;
  optionDecorator?: StyledDecorator;
  isOpenDecorator?: StyledDecorator;
  alignOptions?: 'left' | 'right';
  onOpen?: () => void;
  onRemoveOption?: () => void;
  hideSelectedOption?: boolean;
};

export default styled(function Select(props: PropType) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [value, setValue] = React.useState(props.value);
  const selectedOption = props.options?.find(o => o.value === value);
  const {
    onChangeOption,
    size = FieldSize.Default,
    as = Theme.White,
    selectedValueDecorator,
    optionDecorator,
    isOpenDecorator,
    alignOptions,
    hideSelectedOption,
    onOpen,
    ...restProps
  } = props;

  React.useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const selectOption = React.useCallback(
    (value: string) => {
      setValue(value);
      setIsOpen(false);
      onChangeOption?.(value);
    },
    [onChangeOption, setIsOpen, setValue],
  );

  const onClick = React.useCallback(() => {
    setIsOpen(!isOpen);
    onOpen?.();
  }, [isOpen, onOpen]);

  const close = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const selectValueDynamicStyle = React.useMemo(
    () => (selectedValueDecorator ? selectedValueDecorator : styled(createFieldSizeDecorator(size))),
    [size, selectedValueDecorator],
  );

  const elementRef = React.useRef<HTMLDivElement>(null);
  useOnClickOutside(elementRef, close, isOpen);

  const optionsDecorator = styled(
    alignOptions === 'left' && {
      left: 0,
      minWidth: 'max-content',
    },
    alignOptions === 'right' && {
      right: 0,
      minWidth: 'max-content',
    },
    alignOptions === undefined && {
      left: 0,
      right: 0,
    },
  );

  return (
    <Base ref={elementRef} {...restProps}>
      <input type="hidden" name={props.name} value={value} />
      <SelectValue css={styled(selectValueDynamicStyle, isOpen && isOpenDecorator)} onClick={onClick}>
        <Value>
          {!value && (props.placeholderTransform ? props.placeholderTransform() : props.placeholder)}
          {selectedOption && props.selectedTransform ? props.selectedTransform(selectedOption) : selectedOption?.text}
        </Value>
        {props.onRemoveOption && selectedOption ? (
          <ResetButton
            onClick={e => {
              e.stopPropagation();
              props.onRemoveOption?.();
            }}
          >
            <CloseIcon />
          </ResetButton>
        ) : (
          <ArrowIcon css={createFlipYDecorator(isOpen)} />
        )}
      </SelectValue>
      <OptionsContainer>
        <Options
          css={styled(createThemeDecorator(as), optionsDecorator, stableScrollbarDecorator(props.options.length))}
          visible={isOpen}
        >
          <Dropdown role="listbox">
            {props.options?.map(option => {
              if (hideSelectedOption && value === option.value) {
                return null;
              }

              return (
                <Option
                  css={styled(
                    createSelectDecorator(value === option.value),
                    optionDecorator ? optionDecorator : createFieldSizeDecorator(size),
                    {
                      fontSize: 'inherit',
                    },
                  )}
                  onClick={() => selectOption(option.value)}
                  role="option"
                  aria-selected={value === option.value ? 'true' : 'false'}
                  key={option.value}
                >
                  {props.optionTransform ? props.optionTransform(option) : option.text}
                </Option>
              );
            })}
          </Dropdown>
        </Options>
      </OptionsContainer>
    </Base>
  );
});

const Base = styled.div({
  display: 'flex',
  flexDirection: 'column',
  fontSize: 'inherit',
});

function createSelectDecorator(selected: boolean) {
  return selected
    ? styled({
        fontWeight: 'bold',
      })
    : styled();
}

const SelectValue = styled(ResetButton, {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  zIndex: 4,
  height: '100%',
  border: { xy: { width: 1, style: 'solid', color: t => t.borderStrongColor } },
  backgroundColor: 'white',
});

const textOverflow = styled({
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
});

const Value = styled.span(textOverflow, {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: 40,
});

const ArrowIcon = styled(ChevronDownIcon, {
  marginLeft: t => t.spacing.s,
  flexShrink: 0,
  fontSize: t => t.fontSize.s,
});

const Dropdown = styled.ul(bareListDecorator);

const OptionsContainer = styled.div({
  position: 'relative',
  zIndex: 5,
});

const Option = styled.li(
  textOverflow,
  createMinLargeViewportDecorator({
    border: {
      x: { width: 'thin', style: 'solid', color: t => t.myPagesMenu?.color },
      bottom: { width: 'thin', style: 'solid', color: t => t.myPagesMenu?.color },
    },
  }),
  {
    cursor: 'pointer',
    border: { bottom: { width: 'thin', style: 'solid', color: t => t.myPagesMenu?.color } },
  },
);

function stableScrollbarDecorator(numberOfOptons: number) {
  return numberOfOptons > 6
    ? styled({
        scrollbarGutter: 'stable',
      })
    : undefined;
}

const Options = styled(SlideHeightAnimation, scrollbarDecorator, overflowYDecorator, {
  position: 'absolute',
  top: 0,
  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 15px 40px 5px',
  maxHeight: '400px',
  width: '100%',
});
